import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertTriangle } from 'lucide-react';
import { REPORT_CATEGORIES, type ReportCategory } from './hooks/use-chat-moderation';
import type { ChatMessage } from './hooks/use-league-chat';

interface ChatReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: ChatMessage | null;
  onReport: (category: ReportCategory, description?: string) => Promise<void>;
  isReporting: boolean;
}

export function ChatReportModal({
  open,
  onOpenChange,
  message,
  onReport,
  isReporting,
}: ChatReportModalProps) {
  const [category, setCategory] = useState<ReportCategory | ''>('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!category) {
      setError('Please select a category');
      return;
    }

    setError(null);
    try {
      await onReport(category, description.trim() || undefined);
      // Reset form and close
      setCategory('');
      setDescription('');
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to submit report');
    }
  };

  const handleClose = () => {
    setCategory('');
    setDescription('');
    setError(null);
    onOpenChange(false);
  };

  if (!message) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Report Message
          </DialogTitle>
          <DialogDescription>
            Report this message if it violates community guidelines. The league
            owner will review your report.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Message preview */}
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Message from {message.username}:</p>
            <p className="text-sm line-clamp-3">{message.content}</p>
          </div>

          {/* Category selection */}
          <div className="grid gap-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={category}
              onValueChange={(value) => setCategory(value as ReportCategory)}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(REPORT_CATEGORIES).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="grid gap-2">
            <Label htmlFor="description">Additional details (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide more context about why you're reporting this message..."
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isReporting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isReporting || !category}>
            {isReporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Report'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
